import type {
  Case,
  CaseOutcome,
  FMEAUpdateReview,
  RecommendedAction,
} from '@/versions/v1/schema/case';
import type { PlantRoleId } from '@/versions/v1/schema/plant-role';
import { asCaseTime, clockOnProductionDay, diffMinutes, formatCaseClock, type ShiftWindow } from '@/versions/v1/shared/clock.derive';
import type { SessionState } from '@/versions/v1/schema/scenario';
import type { VizTone } from '@/versions/v1/modules/_viz';
import { ZERO_TOUCH_UNDIAGNOSED } from '@/versions/v1/modules/advisory/branch-labels';
import { assigneeLabel } from '@/versions/v1/modules/advisory/Scene.transform';
import { applyRecommendedActions } from '@/versions/v1/shared/AdvisoryEngine';
import type { AdvisorySessionState } from '@/versions/v1/shared/AdvisoryEngine';
import { DIAGNOSTIC_RULES } from '@/versions/v1/shared/bunker-hill-seed';
import { diagnose } from '@/versions/v1/shared/DiagnosisEngine';
import { effectivenessFor, watchedActionForCase } from '@/versions/v1/shared/EffectivenessEngine';
import { excursions } from '@/versions/v1/shared/SpcEngine';
import { casesForRole } from '@/versions/v1/shared/role-scope';
import { displayCaseTitle } from '@/versions/v1/shared/recovery-vocab.derive';

const SEVERITY_RANK: Record<Case['severity'], number> = { high: 0, med: 1, low: 2, monitor: 3 };
const RULE_BY_ID = new Map(DIAGNOSTIC_RULES.map((row) => [row.id, row]));

export type MetLeadInput = {
  cases: readonly Case[];
  actions: readonly RecommendedAction[];
  reviews: readonly FMEAUpdateReview[];
  outcomes: readonly CaseOutcome[];
};

export type MetLeadRowsRead = {
  rows: MetLeadRow[];
  earlierRows: MetLeadRow[];
};

export type MetLeadRow = {
  caseId: string;
  variableId: string | null;
  signal: { text: string; since: string };
  cause: {
    text: string;
    confidencePct?: number;
    rival?: { text: string; pct: number };
    escalated: boolean;
  };
  change: {
    text: string;
    role: PlantRoleId;
    roleLabel: string;
    actionState?: 'proposed' | 'approved' | 'rejected' | 'done';
    evidenceTarget?: PlantRoleId;
  };
  showsIn: {
    label: string;
    stateLabel: string;
    tone: VizTone;
    tooltip: string;
  };
  learned?: {
    reviewState: string;
    href: string;
  };
};

const EFFECTIVENESS_TONE: Record<string, VizTone> = {
  'awaiting-action': 'neutral',
  watching: 'neutral',
  improving: 'ok',
  'no-change': 'warn',
  worsening: 'bad',
  resolved: 'ok',
  unobservable: 'neutral',
};

const EFFECTIVENESS_LABEL: Record<string, string> = {
  'awaiting-action': 'awaiting action',
  watching: 'watching',
  improving: 'improving',
  'no-change': 'no change',
  worsening: 'worsening',
  resolved: 'resolved',
  unobservable: 'unobservable',
};

/** Sentence end = period + whitespace + capital; bare "." preserves decimals. */
export function firstSentence(text: string): string {
  const match = text.match(/^.*?\.(?=\s+[A-Z]|\s*$)/);
  const sentence = match ? match[0].trim() : text.trim();
  return sentence.replace(/\.{2,}$/, '.');
}

export function leadVariableIds(rows: readonly MetLeadRow[]): string[] {
  return rows.map((row) => row.variableId).filter((id): id is string => Boolean(id));
}

function excursionSince(caseItem: Case, state: SessionState): string {
  const eventId = caseItem.variationEventId;
  if (eventId?.startsWith('ex-')) {
    const match = excursions(state).find((row) => row.id === eventId);
    if (match) return formatCaseClock(match.detectedAt, state.cursor);
  }
  return formatCaseClock(caseItem.raisedAt, state.cursor);
}

function rivalForCase(caseItem: Case, state: SessionState, advisory: AdvisorySessionState) {
  const diagnosis = diagnose(state, advisory);
  const variableId = caseItem.variationEventId?.startsWith('ex-')
    ? caseItem.variationEventId.replace(/^ex-/, '').replace(/-(six-point-trend|beyond-limit|widening-variability|cyclical)$/, '')
    : undefined;
  const candidate = diagnosis.candidates.find((row) => {
    if (variableId) return row.excursion.variableId === variableId;
    if (caseItem.id.startsWith('case-mv-')) {
      const caseVar = caseItem.id.replace(/^case-/, '');
      return row.excursion.variableId === caseVar;
    }
    return row.rule.diagnosis === firstSentence(caseItem.causeCandidate);
  });
  if (!candidate?.rule.rival || candidate.rivalScore <= 0) return undefined;
  const rivalRule = RULE_BY_ID.get(candidate.rule.rival);
  if (!rivalRule) return undefined;
  return { text: rivalRule.diagnosis, pct: Math.round(candidate.rivalScore * 100) };
}

function firstActionForCase(
  caseItem: Case,
  actions: readonly RecommendedAction[],
  advisory: AdvisorySessionState,
): RecommendedAction | undefined {
  return watchedActionForCase(caseItem, actions, advisory);
}

function seedApprovedWithoutStamp(
  caseItem: Case,
  actions: readonly RecommendedAction[],
  advisory: AdvisorySessionState,
): boolean {
  const watched = watchedActionForCase(caseItem, actions, advisory);
  if (!watched) return false;
  const merged = applyRecommendedActions(actions, advisory);
  const row = merged.find((action) => action.id === watched.id);
  return Boolean(
    row &&
      (row.status === 'approved' || row.status === 'done') &&
      !advisory.actionDecisions.get(row.id)?.at &&
      !row.decision?.at,
  );
}

function learnedReviewLabel(review: FMEAUpdateReview | undefined): string {
  if (!review) return 'awaiting Met review';
  if (review.approvalStatus === 'approved') return 'approved';
  if (review.reviewStatus === 'approved') return 'reviewed · awaiting GM';
  return 'awaiting Met review';
}

function learnedChip(
  caseItem: Case,
  outcomes: readonly CaseOutcome[],
  reviews: readonly FMEAUpdateReview[],
  projectSlug: string,
): MetLeadRow['learned'] | undefined {
  const outcome = outcomes.find((row) => row.caseId === caseItem.id && row.proposedRuleId);
  if (!outcome) return undefined;
  const review = reviews.find((row) => row.caseOutcomeId === outcome.id);
  return {
    reviewState: learnedReviewLabel(review),
    href: `/projects/${projectSlug}/advisory?tab=approvals`,
  };
}

function caseStamp(caseItem: Case, state: SessionState): string {
  const eventId = caseItem.variationEventId;
  if (eventId?.startsWith('ex-')) {
    const match = excursions(state).find((row) => row.id === eventId);
    if (match) return match.detectedAt;
  }
  return caseItem.raisedAt;
}

function buildMetLeadRow(
  caseItem: Case,
  input: MetLeadInput,
  state: SessionState,
  advisory: AdvisorySessionState,
  projectSlug: string,
  diagnosis: ReturnType<typeof diagnose>,
): MetLeadRow {
    const effectiveness = effectivenessFor(caseItem, state, advisory, diagnosis, input.actions);
    const escalated = caseItem.branch === 'escalate' || caseItem.state === 'escalated';
    const openEvidence = caseItem.evidenceRequests.find((row) => row.status === 'open');
    const action = firstActionForCase(caseItem, input.actions, advisory);
    const rival = rivalForCase(caseItem, state, advisory);

    let changeText = action?.text ?? '—';
    let changeRole: PlantRoleId = caseItem.assignedTo;
    let actionState = action?.status;
    let evidenceTarget: PlantRoleId | undefined;

    if (caseItem.branch === 'ask' && openEvidence) {
      changeText = openEvidence.text;
      changeRole = openEvidence.target;
      evidenceTarget = openEvidence.target;
      actionState = undefined;
    }

    const causeText = caseItem.causeCandidate.startsWith('No structured rule matched')
      ? ZERO_TOUCH_UNDIAGNOSED
      : caseItem.causeCandidate;

    const showsInStateLabel =
      effectiveness.state === 'awaiting-action' && seedApprovedWithoutStamp(caseItem, input.actions, advisory)
        ? 'approved · watch not started'
        : (EFFECTIVENESS_LABEL[effectiveness.state] ?? effectiveness.state);

    const trend =
      effectiveness.after?.trend ?? effectiveness.before?.trend;
    const signalLabel =
      trend && trend !== 'stable'
        ? `${effectiveness.label} · ${trend}`
        : effectiveness.label;

    return {
      caseId: caseItem.id,
      variableId: effectiveness.variableId,
      signal: { text: displayCaseTitle(caseItem, state), since: excursionSince(caseItem, state) },
      cause: {
        text: causeText,
        confidencePct: escalated ? undefined : caseItem.aiConfidencePct,
        rival,
        escalated,
      },
      change: {
        text: changeText,
        role: changeRole,
        roleLabel: assigneeLabel(changeRole),
        actionState,
        evidenceTarget,
      },
      showsIn: {
        label: signalLabel,
        stateLabel: showsInStateLabel,
        tone: EFFECTIVENESS_TONE[effectiveness.state] ?? 'neutral',
        tooltip: effectiveness.sentence,
      },
      learned: learnedChip(caseItem, input.outcomes, input.reviews, projectSlug),
    };
}

export function metLeadRows(
  input: MetLeadInput,
  state: SessionState,
  advisory: AdvisorySessionState,
  projectSlug: string,
  window?: ShiftWindow,
): MetLeadRowsRead {
  const diagnosis = diagnose(state, advisory);
  const scoped = casesForRole('metallurgist', input.cases);
  const sorted = [...scoped].sort((a, b) => {
    const sev = SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity];
    if (sev !== 0) return sev;
    const conf = (b.aiConfidencePct ?? 0) - (a.aiConfidencePct ?? 0);
    if (conf !== 0) return conf;
    return a.raisedAt.localeCompare(b.raisedAt);
  });

  const rows = sorted.map((caseItem) =>
    buildMetLeadRow(caseItem, input, state, advisory, projectSlug, diagnosis),
  );

  if (!window) {
    return { rows, earlierRows: [] };
  }

  const inWindow: MetLeadRow[] = [];
  const earlierRows: MetLeadRow[] = [];
  for (let i = 0; i < sorted.length; i++) {
    const caseItem = sorted[i]!;
    const stamp = caseStamp(caseItem, state);
    const full = stamp.includes('T') ? asCaseTime(stamp) : clockOnProductionDay(stamp, state.cursor);
    if (diffMinutes(full, window.start) < 0) {
      earlierRows.push(rows[i]!);
    } else {
      inWindow.push(rows[i]!);
    }
  }

  return { rows: inWindow, earlierRows };
}
